import { useTranslation } from 'react-i18next';
import { Avatar, Flex, Table, Text, UnreadDot } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { conversationUnreadCount, isConversationUnread, type Conversation } from '@/services';
import { formatRelativeTimestamp } from '@/utils/date';
import { ConversationContextMenu } from './ConversationMenu';

const MAX_PREVIEW_LENGTH = 20;

function truncatePreview(text: string): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  const chars = Array.from(normalized);
  if (chars.length <= MAX_PREVIEW_LENGTH) return normalized;
  return chars.slice(0, MAX_PREVIEW_LENGTH).join('') + '…';
}

interface ConversationRowProps {
  conversation: Conversation;
  name: string;
  photoUrl?: string;
  onClick: () => void;
}

export function ConversationRow({ conversation, name, photoUrl, onClick }: ConversationRowProps) {
  const { t } = useTranslation('common');
  const { t: tChat } = useTranslation('chat');
  const { user } = useCurrentUser();
  const unreadLabel = t('actions.unread');
  const unreadCount = user ? conversationUnreadCount(conversation, user.uid) : 0;
  const hasUnread = user ? isConversationUnread(conversation, user.uid) : false;
  const timestamp = formatRelativeTimestamp(conversation.lastMessageAt, {
    yesterdayLabel: t('time.yesterday'),
  });
  const displayPreview = truncatePreview(
    conversation.lastMessage || tChat('inbox.previewFallback')
  );
  return (
    <ConversationContextMenu conversation={conversation} partnerName={name}>
      <Table.Row
        onClick={onClick}
        style={{ cursor: 'pointer', userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        <Table.Cell>
          <Avatar name={name} src={photoUrl} size="3" radius="full" />
        </Table.Cell>
        <Table.Cell>
          <Flex direction="column" gap="1">
            <Text size="2" weight="bold" truncate>
              {name}
            </Text>
            <Text size="1" color="gray" as="div" truncate>
              {displayPreview}
            </Text>
          </Flex>
        </Table.Cell>
        <Table.Cell justify="end">
          <Flex direction="column" align="end" gap="1">
            <Flex direction="row" align="center" gap="2">
              <Text size="1" color={hasUnread ? 'red' : 'gray'}>
                {timestamp}
              </Text>
              {hasUnread && (
                <UnreadDot
                  size="1"
                  aria-label={unreadCount > 0 ? `${unreadCount} ${unreadLabel}` : unreadLabel}
                >
                  {unreadCount || null}
                </UnreadDot>
              )}
            </Flex>
          </Flex>
        </Table.Cell>
      </Table.Row>
    </ConversationContextMenu>
  );
}
